/**
 * Idle / multi-tab user-activity helpers for Layout auto-logout.
 */

export const IDLE_WARNING_MS = 25 * 60 * 1000; // 25 minutes
export const IDLE_LOGOUT_MS = 30 * 60 * 1000; // 30 minutes
export const ACTIVITY_CHECK_INTERVAL_MS = 1000;
export const ACTIVITY_THROTTLE_MS = 1000;
export const ACTIVITY_CHANNEL_NAME = "activity-channel";
export const IDLE_WARNING_MESSAGE =
  "You will be logged out shortly. Please activate your screen.";

export const ACTIVITY_MESSAGE_TYPES = Object.freeze({
  TAB_INIT: "TAB_INIT",
  TAB_ACK: "TAB_ACK",
  USER_ACTIVITY: "USER_ACTIVITY",
  TAB_CLOSING: "TAB_CLOSING",
});

/** Events that should always update last-activity immediately. */
export const ACTIVITY_EVENTS = Object.freeze([
  "mousedown",
  "keydown",
  "wheel",
  "touchstart",
  "touchend",
  "touchcancel",
  "pointerdown",
  "pointerup",
  "pointercancel",
]);

/**
 * High-frequency events — throttle these so Layout does not spam
 * BroadcastChannel / state updates on every mouse pixel.
 */
export const THROTTLED_ACTIVITY_EVENTS = Object.freeze([
  "mousemove",
  "touchmove",
  "pointermove",
]);

export function hasAccessToken() {
  try {
    return Boolean(localStorage.getItem("access_token"));
  } catch {
    return false;
  }
}

export function getInitialLastActivityTimestamp() {
  return hasAccessToken() ? Date.now() : 0;
}

/**
 * Stable unique tab id. Prefers crypto.randomUUID when available.
 */
export function createTabId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `tab-${crypto.randomUUID()}`;
  }
  return `tab-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

/**
 * Leading-edge throttle for high-frequency activity handlers.
 */
export function createThrottle(fn, waitMs = ACTIVITY_THROTTLE_MS) {
  let lastCallAt = 0;

  const throttled = (...args) => {
    const now = Date.now();
    if (now - lastCallAt >= waitMs) {
      lastCallAt = now;
      return fn(...args);
    }
    return undefined;
  };

  throttled.cancel = () => {
    lastCallAt = 0;
  };

  return throttled;
}

export function createActivityMessage(type, tabId, timestamp = Date.now()) {
  return { type, tabId, timestamp };
}

/**
 * Pure reducer for BroadcastChannel activity messages.
 * Returns patch instructions so the React hook stays thin.
 */
export function reduceActivityChannelMessage(
  message,
  { currentTabId, lastActivityTimestamp }
) {
  if (!message?.type) {
    return null;
  }

  const { type, tabId: sourceTabId, timestamp } = message;

  switch (type) {
    case ACTIVITY_MESSAGE_TYPES.TAB_INIT:
      return {
        reply: createActivityMessage(
          ACTIVITY_MESSAGE_TYPES.TAB_ACK,
          currentTabId
        ),
        incrementTabs: true,
      };

    case ACTIVITY_MESSAGE_TYPES.TAB_ACK:
      if (sourceTabId !== currentTabId) {
        return { incrementTabs: true };
      }
      return null;

    case ACTIVITY_MESSAGE_TYPES.USER_ACTIVITY:
      if (sourceTabId !== currentTabId) {
        return {
          lastActivityTimestamp: timestamp ?? lastActivityTimestamp,
          clearWarning: true,
        };
      }
      return null;

    case ACTIVITY_MESSAGE_TYPES.TAB_CLOSING:
      if (sourceTabId !== currentTabId) {
        return { decrementTabs: true };
      }
      return null;

    default:
      return null;
  }
}

/**
 * Decide idle warning / logout from elapsed inactive time.
 */
export function getIdleAction(inactiveTimeMs, { warningShown }) {
  if (inactiveTimeMs >= IDLE_LOGOUT_MS) {
    return "logout";
  }
  if (inactiveTimeMs >= IDLE_WARNING_MS && !warningShown) {
    return "warn";
  }
  return null;
}
